import { LowerCasePipe, NgClass, NgStyle } from "@angular/common";
import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { createPopper } from "@popperjs/core";
import { DocumentEventService } from "@ramudden/services";
import { SelectItem } from "primeng/api";

export class TwoLevelSelectValue {
    constructor(
        public option1: any,
        public option2: any,
    ) {}
}

export class TwoLevelSelectOption implements SelectItem {
    label?: string;
    value: any;
    styleClass?: string;
    icon?: string;
    title?: string;
    disabled?: boolean;

    relations: SelectItem[];
}

@Component({
    selector: "app-two-level-select",
    templateUrl: "./two-level-select.component.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: TwoLevelSelectComponent,
        },
    ],
    imports: [NgClass, NgStyle, LowerCasePipe, TranslateModule],
})
export class TwoLevelSelectComponent implements ControlValueAccessor, OnDestroy, OnChanges {
    // Requires that elements on first level have fullfilied relations property representing associated SelectItems on second level
    @Input() configuration: TwoLevelSelectOption[];
    firstLevelOptions: SelectItem[];
    secondLevelOptions: SelectItem[];

    @Input() closeDropDownAfterFirstLevelChange = true;
    @Input() closeDropDownAfterSecondLevelChange = true;

    @Input() wordBetweenOptionsTranslation: string;
    @Input() ngStyle?: any;
    @Input() ngStyleHeader?: any;
    @Input() ngStyleExpandablePart?: any;

    @Output() onChange = new EventEmitter<TwoLevelSelectValue>();

    selectedFirstLevel: SelectItem;
    selectedSecondLevel: SelectItem;

    expanded = false;
    touched = false;
    disabled = false;
    private _popper: any;

    value: TwoLevelSelectValue;
    onChangeHandler: (value: any) => {};
    onTouchedHandler: () => {};

    @ViewChild("dropDown", { static: true }) dropDown: ElementRef<HTMLDivElement>;
    @ViewChild("dropDownPart", { static: true }) dropDownPart: ElementRef<HTMLDivElement>;

    private readonly renderer = inject(Renderer2);
    private readonly documentService = inject(DocumentEventService);
    private readonly clickEventName = "popup-click-event-handler";

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["configuration"]) {
            if (this.expanded) {
                this.toggleExpanded();
            }

            this.handleConfigurationChange();
        }
    }

    ngOnDestroy(): void {
        if (this.expanded) {
            this._popper && this._popper.destroy();
            this.renderer.setStyle(this.dropDownPart.nativeElement, "display", "none");

            this.documentService.removeOnClick(this.clickEventName);
        }
    }

    private handleConfigurationChange() {
        this.createLevels();
        if (!this.selectedFirstLevel) {
            return;
        }

        const selectedFirstOption = this.configuration.find((x) => x.value === this.selectedFirstLevel.value);
        if (selectedFirstOption) {
            const selectedSecondOptionExits = selectedFirstOption.relations.some(
                (x) => x.value === this.selectedSecondLevel.value,
            );

            if (!selectedSecondOptionExits) {
                const configurationOption = this.configuration.find((x) => x.value === this.selectedFirstLevel.value);
                this.filterSecondLevelOptions(configurationOption);
                this.valueChanged();
            }
        } else {
            this.handleFirstLevelSelect(this.firstLevelOptions.takeFirstOrDefault());
        }
    }

    public handleFirstLevelSelect(selectedElement: SelectItem) {
        const configurationOption = this.configuration.find((x) => x.label === selectedElement.label);

        if (!this.configuration || !configurationOption.relations) {
            return;
        }

        this.markAsTouched();
        if (this.selectedFirstLevel && this.selectedFirstLevel === selectedElement) {
            return;
        }

        this.selectedFirstLevel = selectedElement;
        this.filterSecondLevelOptions(configurationOption);

        this.valueChanged();
        if (this.closeDropDownAfterFirstLevelChange && this.expanded) {
            this.toggleExpanded();
        }
    }

    public handleSecondLevelSelect(selectedElement: SelectItem) {
        if ((<SelectItem>selectedElement).disabled) {
            return;
        }
        this.markAsTouched();

        if (selectedElement === this.selectedSecondLevel) {
            return;
        }

        this.selectedSecondLevel = selectedElement;

        this.valueChanged();
        if (this.closeDropDownAfterSecondLevelChange && this.expanded) {
            this.toggleExpanded();
        }
    }

    public toggleExpanded() {
        if (this.expanded) {
            this._popper && this._popper.destroy();
            this.documentService.removeOnClick(this.clickEventName);
        } else {
            this._popper = createPopper(this.dropDown.nativeElement, this.dropDownPart.nativeElement, {
                placement: "bottom-start",
            });

            this.documentService.addOnClick(this.clickEventName, this.dropDownPart.nativeElement, () => {
                this.toggleExpanded();
            });
        }

        this.renderer.setStyle(this.dropDownPart.nativeElement, "display", this.expanded ? "none" : "block");
        this.expanded = !this.expanded;
    }

    /// #region Form

    writeValue(obj: any): void {
        if (!(obj instanceof TwoLevelSelectValue)) {
            return;
        }

        this.value = <TwoLevelSelectValue>obj;
        if (!this.configuration || !this.firstLevelOptions || !this.secondLevelOptions) {
            return;
        }

        this.selectedFirstLevel =
            this.firstLevelOptions?.find((x) => x.value === (<TwoLevelSelectValue>obj).option1) ??
            this.firstLevelOptions?.takeFirstOrDefault();
        this.selectedSecondLevel = this.secondLevelOptions?.find((x) => x.value === (<TwoLevelSelectValue>obj).option2);

        if (!this.selectedFirstLevel) {
            return;
        }

        this.filterSecondLevelOptions(this.configuration.find((x) => x.value === (<TwoLevelSelectValue>obj).option1));

        if (this.selectedSecondLevel?.value !== obj?.option2 || this.selectedFirstLevel?.value !== obj?.option1) {
            // if selected value is not possible through current configuration, we need to select first available combination
            this.valueChanged();
        }
    }

    registerOnChange(fn: any): void {
        this.onChangeHandler = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouchedHandler = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.disabled && this.expanded) {
            this.toggleExpanded();
        }
    }

    private markAsTouched() {
        if (!this.touched) {
            if (this.onTouchedHandler) {
                this.onTouchedHandler();
            }

            this.touched = true;
        }
    }

    /// #endregion Form

    // After selecting first level to filter second level or when setting value of component if wrong combination is configured to select appropriate second level
    // Called after setting selectedFirstLevel
    private filterSecondLevelOptions(selectedFirstLevel: TwoLevelSelectOption) {
        if (!selectedFirstLevel || !selectedFirstLevel.relations || selectedFirstLevel.relations.length === 0) {
            return;
        }

        for (const availableSecondLevelOption of selectedFirstLevel.relations) {
            (<SelectItem>availableSecondLevelOption).disabled = false;
        }

        const otherSecondLevelOptions = this.secondLevelOptions.filter(
            (x) => !selectedFirstLevel.relations.contains(x),
        );
        for (const otherSecondLevelOption of otherSecondLevelOptions) {
            (<SelectItem>otherSecondLevelOption).disabled = true;
        }

        if (
            !this.selectedSecondLevel ||
            !selectedFirstLevel.relations.some((x) => x.value === this.selectedSecondLevel.value)
        ) {
            this.selectedSecondLevel = selectedFirstLevel.relations.takeFirstOrDefault();
        }
    }

    private createLevels() {
        this.firstLevelOptions = this.configuration;
        this.secondLevelOptions = this.configuration
            .map((x) => x.relations)
            .sortByDescending((x) => x.length)
            .takeFirstOrDefault();
    }

    private valueChanged() {
        if (this.onChangeHandler) {
            const value = {
                option1: this.selectedFirstLevel?.value,
                option2: this.selectedSecondLevel?.value,
            } as TwoLevelSelectValue;

            this.onChangeHandler(value);
            this.onChange.emit(value);
        }
    }
}
